export class GeneralUtils {

    // ===============================
    // 1. OBJECT OPERATIONS
    // ===============================

    static extend(dest: any, src: any): any {
        for (const prop in src) {
            if (src.hasOwnProperty(prop)) {
                dest[prop] = src[prop];
            }
        }
        return dest;
    }

    static clone(c: any): any {
        if (c == null)
            return null;

        let data = {};
        for (let prop in c) {
            data[prop] = c[prop];
        }
        return data;
    }

    // static copy(aObject: any): any {
    //     return JSON.parse(JSON.stringify(aObject));
    // }
    static copy(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
          return obj;
        }
      
        if (obj instanceof Date) {
          return new Date(obj.getTime());
        }
      
        if (Array.isArray(obj)) {
          return obj.map(item => this.copy(item));
        }
      
        const copied: any = {};
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            copied[key] = this.copy(obj[key]);
          }
        }
      
        return copied;
      }
      
    static camelCaseObject(o: any): any {
        let newO, origKey, value;
        if (o instanceof Array) {
            newO = [];
            for (origKey in o) {
                value = o[origKey];
                if (typeof value === 'object') {
                    value = GeneralUtils.camelCaseObject(value);
                }
                newO.push(value);
            }
        } else {
            newO = {};
            for (origKey in o) {
                if (o.hasOwnProperty(origKey)) {
                    value = o[origKey];
                    if (typeof value === 'object' && !!value && value.constructor === Object) {
                        value = GeneralUtils.camelCaseObject(value);
                    }
                    // Convert to camelCase
                    const newKey = origKey.toLowerCase().replace(/([-_][a-z])/g, group =>
                        group
                            .toUpperCase()
                            .replace('-', '')
                            .replace('_', '')
                    );
                    newO[newKey] = value;
                }
            }
        }
        return newO;
    }

    // ===============================
    // 2. DOM OPERATIONS  
    // ===============================

    static selectOne(selector: string): Element | null {
        return document.querySelector(selector);
    }

    static selectAll(selector: string): NodeListOf<Element> {
        return document.querySelectorAll(selector);
    }

    static create(html: string): Element | null {
        const placeholder = document.createElement("div");
        placeholder.innerHTML = html;
        return placeholder.childNodes[0] as Element;
    }

    static nodeListEach(nodeList: NodeList, callback: Function): void {
        for (let i: number = 0; i < nodeList.length; i++) {
            callback(i, nodeList[i]);
        }
    }

    static nodeListLast(nodeList: NodeList): Element | null {
        return nodeList.length > 0 ? nodeList[nodeList.length - 1] as Element : null;
    }

    // ===============================
    // 3. ELEMENT OPERATIONS
    // ===============================

    static getAttribute(element: Element, name: string): string | null {
        return element.getAttribute(name);
    }

    static setAttribute(element: Element, name: string, value: string): Element {
        element.setAttribute(name, value);
        return element;
    }

    static getParent(element: Element): Element | null {
        return element.parentElement;
    }

    static hasMatches(element: Element, selector: string): boolean {
        if ((element as any).matches != null) {
            return (element as any).matches(selector);
        } else if ((element as any).msMatchesSelector != null) {
            return (element as any).msMatchesSelector(selector);
        }
        return false;
    }

    static closest(element: Element, selector: string): Element | null {
        let el: Element | null = element;
        if ((el as any).closest != null) {
            return (el as any).closest(selector);
        } else {
            while (el) {
                if (this.hasMatches(el, selector)) {
                    return el;
                }
                el = el.parentElement;
            }
        }
        return null;
    }

    static findFirst(element: Element, selector: string): Element | null {
        const _descend = (node: Element): Element | null => {
            const nodeList: NodeList = node.childNodes;
            for (let i = 0; i < nodeList.length; i++) {
                if (nodeList[i].nodeName.toLowerCase() === selector.toLowerCase()) {
                    return nodeList[i] as Element;
                }
            }
            return _descend(node);
        };
        return _descend(element);
    }

    static append(element: Element, html: string): Element {
        element.insertAdjacentHTML("beforeend", html);
        return element;
    }

    static empty(element: Element): Element {
        element.innerHTML = "";
        return element;
    }

    static remove(element: Element): Element {
        const parent = element.parentNode;
        if (parent) {
            parent.removeChild(element);
        }
        return element;
    }

    static clean(element: HTMLElement): HTMLElement {
        element.innerHTML = '';
        return element;
    }

    static getValue(element: HTMLElement): string {
        if (element instanceof HTMLInputElement ||
            element instanceof HTMLTextAreaElement ||
            element instanceof HTMLSelectElement) {
            return element.value;
        }
        return element.textContent || '';
    }

    static setValue(element: HTMLElement, value: string): HTMLElement {
        if (element instanceof HTMLInputElement ||
            element instanceof HTMLTextAreaElement ||
            element instanceof HTMLSelectElement) {
            element.value = value;
        } else {
            element.textContent = value;
        }
        return element;
    }

    static isHidden(element: Element): boolean {
        return (element as HTMLElement).offsetParent === null;
    }

    static show(element: Element, displayType: string = 'block'): Element {
        (element as HTMLElement).style.display = displayType;
        return element;
    }

    static hide(element: Element): Element {
        (element as HTMLElement).style.display = 'none';
        return element;
    }

    static toggle(element: Element): Element {
        if (this.isHidden(element)) {
            this.show(element);
        } else {
            this.hide(element);
        }
        return element;
    }

    static addClass(element: Element, className: string): Element {
        element.classList.add(className);
        return element;
    }

    static removeClass(element: Element, className: string): Element {
        element.classList.remove(className);
        return element;
    }

    static hasClass(element: Element, className: string): boolean {
        return element.classList.contains(className);
    }

    // ===============================
    // 4. VALIDATION & UTILITIES
    // ===============================

    static isEmpty(val: any): boolean {
        return val == null || val === undefined || val === '' || val.length === 0;
    }

    static parseResponseJSON(xhr: XMLHttpRequest): any {
        try {
            return JSON.parse(xhr.responseText);
        } catch (e) {
            console.error('Failed to parse JSON response:', e);
            return null;
        }
    }

    static bin2string(array: any[]): string {
        return String.fromCharCode.apply(String, array);
    }

    // ===============================
    // 5. ASYNC OPERATIONS
    // ===============================

    static delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
} 